/**
 * Role-keyed chat store. One thread per `AgentRole` (CEO, Ops). The WS
 * dispatcher in `api/ws.ts` pushes streaming deltas, tool calls, and turn
 * boundaries; the chat panel/hook renders from here.
 *
 * Replaces the v0 `stores/ceo.ts` which keyed on `(company_id)` only and
 * couldn't separate the CEO and Ops conversations.
 */

import { create } from "zustand";
import type { AgentRole } from "@paperclip-cloud/shared";

export type ChatAuthor = "user" | "agent" | "system" | "tool";

export interface ToolCall {
  id: string;
  tool: string;
  args: unknown;
  result?: unknown;
  error?: string;
  at: number;
}

export interface ChatMessage {
  id: string;
  author: ChatAuthor;
  content: string;
  /** True while the agent is still streaming tokens for this message. */
  streaming: boolean;
  toolCalls: ToolCall[];
  at: number;
  /**
   * When set, this message has been folded into the referenced compression
   * summary and is hidden from the chat panel by default. Newly-streamed
   * messages always start with this `null`; the field is populated when a
   * fresh history fetch arrives after a compression turn.
   */
  compressed_into_id?: string | null;
}

export interface ChatThread {
  /** The active company this thread belongs to. */
  companyId: string | null;
  messages: ChatMessage[];
  /** Stream cursor: the message id we're currently appending deltas to. */
  streaming: { messageId: string; deltas: string[] } | null;
  /** Tool calls indexed by id for O(1) update lookups. */
  toolCalls: Record<string, ToolCall>;
  draft: string;
}

const EMPTY_THREAD: ChatThread = {
  companyId: null,
  messages: [],
  streaming: null,
  toolCalls: {},
  draft: "",
};

interface ChatState {
  /** Threads keyed by AgentRole. Each thread is scoped to the active company. */
  threads: Record<AgentRole, ChatThread>;
  /**
   * The company the currently-rendered threads belong to. When this changes
   * we wipe the threads — they're per-company and we don't bother caching
   * across switches in v1.
   */
  scopedCompanyId: string | null;

  ensureCompany: (companyId: string) => void;
  appendUser: (role: AgentRole, content: string) => string;
  appendDelta: (role: AgentRole, messageId: string, delta: string) => void;
  appendToolCall: (
    role: AgentRole,
    messageId: string,
    toolCall: ToolCall,
  ) => void;
  updateToolCall: (
    role: AgentRole,
    messageId: string,
    callId: string,
    patch: Partial<ToolCall>,
  ) => void;
  endStream: (role: AgentRole, messageId: string) => void;
  loadHistory: (role: AgentRole, messages: ChatMessage[]) => void;
  setDraft: (role: AgentRole, draft: string) => void;
  clear: (role?: AgentRole) => void;
}

function freshThread(companyId: string | null): ChatThread {
  return { ...EMPTY_THREAD, companyId };
}

export const useChatStore = create<ChatState>((set) => ({
  threads: {
    ceo: { ...EMPTY_THREAD },
    ops: { ...EMPTY_THREAD },
  },
  scopedCompanyId: null,

  ensureCompany: (companyId) =>
    set((s) => {
      if (s.scopedCompanyId === companyId) return s;
      return {
        scopedCompanyId: companyId,
        threads: {
          ceo: freshThread(companyId),
          ops: freshThread(companyId),
        },
      };
    }),

  appendUser: (role, content) => {
    const id = `local_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
    set((s) => {
      const thread = s.threads[role];
      const next: ChatMessage = {
        id,
        author: "user",
        content,
        streaming: false,
        toolCalls: [],
        at: Date.now(),
      };
      return {
        threads: {
          ...s.threads,
          [role]: {
            ...thread,
            messages: [...thread.messages, next],
            draft: "",
          },
        },
      };
    });
    return id;
  },

  appendDelta: (role, messageId, delta) =>
    set((s) => {
      const thread = s.threads[role];
      const idx = thread.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) {
        // First delta — synthesise the assistant message.
        const next: ChatMessage = {
          id: messageId,
          author: "agent",
          content: delta,
          streaming: true,
          toolCalls: [],
          at: Date.now(),
        };
        return {
          threads: {
            ...s.threads,
            [role]: {
              ...thread,
              messages: [...thread.messages, next],
              streaming: { messageId, deltas: [delta] },
            },
          },
        };
      }
      const messages = thread.messages.slice();
      messages[idx] = {
        ...messages[idx],
        content: messages[idx].content + delta,
        streaming: true,
      };
      const cursor =
        thread.streaming && thread.streaming.messageId === messageId
          ? { messageId, deltas: [...thread.streaming.deltas, delta] }
          : { messageId, deltas: [delta] };
      return {
        threads: {
          ...s.threads,
          [role]: { ...thread, messages, streaming: cursor },
        },
      };
    }),

  appendToolCall: (role, messageId, toolCall) =>
    set((s) => {
      const thread = s.threads[role];
      const idx = thread.messages.findIndex((m) => m.id === messageId);
      const calls = { ...thread.toolCalls, [toolCall.id]: toolCall };
      if (idx === -1) {
        // No assistant message yet — stub one so the card has somewhere to live.
        const next: ChatMessage = {
          id: messageId,
          author: "agent",
          content: "",
          streaming: true,
          toolCalls: [toolCall],
          at: Date.now(),
        };
        return {
          threads: {
            ...s.threads,
            [role]: {
              ...thread,
              messages: [...thread.messages, next],
              toolCalls: calls,
            },
          },
        };
      }
      const messages = thread.messages.slice();
      messages[idx] = {
        ...messages[idx],
        toolCalls: [...messages[idx].toolCalls, toolCall],
      };
      return {
        threads: {
          ...s.threads,
          [role]: { ...thread, messages, toolCalls: calls },
        },
      };
    }),

  updateToolCall: (role, messageId, callId, patch) =>
    set((s) => {
      const thread = s.threads[role];
      const idx = thread.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) return s;
      const messages = thread.messages.slice();
      const updated = messages[idx].toolCalls.map((c) =>
        c.id === callId ? { ...c, ...patch } : c,
      );
      messages[idx] = { ...messages[idx], toolCalls: updated };
      const callsIndex = { ...thread.toolCalls };
      if (callsIndex[callId]) callsIndex[callId] = { ...callsIndex[callId], ...patch };
      return {
        threads: {
          ...s.threads,
          [role]: { ...thread, messages, toolCalls: callsIndex },
        },
      };
    }),

  endStream: (role, messageId) =>
    set((s) => {
      const thread = s.threads[role];
      const idx = thread.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) {
        return {
          threads: {
            ...s.threads,
            [role]: { ...thread, streaming: null },
          },
        };
      }
      const messages = thread.messages.slice();
      messages[idx] = { ...messages[idx], streaming: false };
      return {
        threads: {
          ...s.threads,
          [role]: { ...thread, messages, streaming: null },
        },
      };
    }),

  loadHistory: (role, messages) =>
    set((s) => ({
      threads: {
        ...s.threads,
        [role]: {
          ...s.threads[role],
          messages,
          streaming: null,
          toolCalls: messages
            .flatMap((m) => m.toolCalls)
            .reduce<Record<string, ToolCall>>((acc, c) => {
              acc[c.id] = c;
              return acc;
            }, {}),
        },
      },
    })),

  setDraft: (role, draft) =>
    set((s) => ({
      threads: { ...s.threads, [role]: { ...s.threads[role], draft } },
    })),

  clear: (role) =>
    set((s) => {
      if (role) {
        return {
          threads: {
            ...s.threads,
            [role]: freshThread(s.scopedCompanyId),
          },
        };
      }
      return {
        threads: {
          ceo: freshThread(s.scopedCompanyId),
          ops: freshThread(s.scopedCompanyId),
        },
      };
    }),
}));

/** Selector — returns the thread for a given role. */
export function useChatThread(role: AgentRole): ChatThread {
  return useChatStore((s) => s.threads[role]);
}
